import React, { useState, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
  Grid,
  Input,
  Button,
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import sofiaRegular from '~/assets/fonts/proximanova-regular-webfont.woff';
import sofiaRegular2 from '~/assets/fonts/proximanova-regular-webfont.woff2';
import { closeDialog } from '~/store/legacy/actions/dialogActions';
import { addPaymentMethod } from '~/store/legacy/actions/paymentActions';
import ChowbusForm from '~/components/ChowbusForm';
import Subtitle from '../ui-kit/Subtitle/Subtitle';
import useStyles from './CardDialog.style';
import { useStripe } from '~/hooks/useStripe';

/**
 * Dialog used to add (and eventually edit) card details
 * @param {object} props
 * @param {boolean} props.isUSAUser
 * @param {boolean} props.isAlipay
 */
function CardDialog(props) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { stripe, isStripeReady } = useStripe();

  const { isUSAUser, isAlipay } = props;

  const [fullName, setFullName] = useState('');
  const [fullNameError, setFullNameError] = useState(false);
  const [postalCode, setPostalCode] = useState(null);
  const [postalCodeError, setPostalCodeError] = useState(false);
  const [addDisabled, setAddDisabled] = useState(true);

  const [cardNumberElement, setCardNumberElement] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [cardExpiryElement, setCardExpiryElement] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [cardCvcElement, setCardCvcElement] = useState(null);
  const [completed, setCompleted] = useState({
    fullName: false,
    postalCode: false,
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false,
  });

  const cardNumberRef = React.createRef();
  const cardExpiryRef = React.createRef();
  const cardCvcRef = React.createRef();

  useEffect(() => {
    if (isStripeReady) {
      const { cardNumber, cardExpiry, cardCvc } = createStripePaymentElements({
        cardNumberRef,
        cardExpiryRef,
        cardCvcRef,
        handleStripeChange,
      });
      setCardNumberElement(cardNumber);
      setCardExpiryElement(cardExpiry);
      setCardCvcElement(cardCvc);
    }
  }, [stripe, isStripeReady]);

  useEffect(() => {
    const allCompleted = Object.values(completed).every((val) => val);
    setAddDisabled(!allCompleted);
  }, [completed]);

  const createStripePaymentElements = ({
    cardNumberRef,
    cardExpiryRef,
    cardCvcRef,
    handleStripeChange,
  }) => {
    // sets up stripe elements and mounts them to their ref
    if (!isStripeReady) {
      return;
    }

    const elements = stripe.elements({
      fonts: [
        {
          family: 'proximanova-regular',
          src: `url(${sofiaRegular}) format("woff") url(${sofiaRegular2}) format('woff2');`,
        },
      ],
    });

    const style = {
      base: {
        fontFamily: '"proximanova-regular", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '17px',
        '::placeholder': {
          color: '#9B9B9B',
        },
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a',
      },
    };

    const cardNumber = elements.create('cardNumber', {
      style,
      placeholder: '',
      iconStyle: 'solid',
      showIcon: true,
    });
    const cardExpiry = elements.create('cardExpiry', {
      style,
    });
    const cardCvc = elements.create('cardCvc', {
      style,
      placeholder: '',
    });
    cardNumber.addEventListener('change', handleStripeChange);
    cardExpiry.addEventListener('change', handleStripeChange);
    cardCvc.addEventListener('change', handleStripeChange);
    cardNumber.mount(cardNumberRef.current);
    cardExpiry.mount(cardExpiryRef.current);
    cardCvc.mount(cardCvcRef.current);
    return { cardNumber, cardExpiry, cardCvc };
  };

  const handleStripeChange = (e) => {
    if (!e.error) {
      setCompleted((completed) => {
        return {
          ...completed,
          ...{
            [e.elementType]: e.complete,
          },
        };
      });
    }
  };

  const handleCancel = () => {
    dispatch(closeDialog());
  };

  const handleFullName = (e) => {
    const { value } = e.target;
    setFullName(value);
    const isFullNameValid = !!value.length;

    if (postalCodeError && isFullNameValid) {
      // If there already is an error but it becomes valid, remove the error
      setFullNameError(false);
    }
    setCompleted({
      ...completed,
      ...{
        fullName: isFullNameValid,
      },
    });
  };

  const handleBlurFullName = () => {
    if (!completed.fullName) {
      setFullNameError(true);
    }
  };

  const postalCodeValidator = ChowbusForm.validators.postalCodeValidatorFactory('US');
  const handlePostalCode = (e) => {
    const { value } = e.target;
    setPostalCode(value);
    const isValidPostalCode = isUSAUser ? postalCodeValidator(value) === null : true;

    if (postalCodeError && isValidPostalCode) {
      // If there already is an error but it becomes valid, remove the error
      setPostalCodeError(false);
    }

    setCompleted({
      ...completed,
      ...{
        postalCode: isValidPostalCode,
      },
    });
  };

  const handleBlurPostalCode = () => {
    if (!completed.postalCode) {
      setPostalCodeError(true);
    }
  };

  const handleAddCard = async () => {
    const { paymentMethod } = await stripe.createPaymentMethod('card', cardNumberElement, {
      billing_details: {
        name: fullName,
        address: {
          postal_code: postalCode,
        },
      },
    });
    if (paymentMethod) {
      // close the dialog first so errors and loading spinner can be seen
      dispatch(closeDialog());
      await dispatch(addPaymentMethod({ method: paymentMethod, showAlerts: false, isAlipay }));
    }
  };

  return (
    <>
      <DialogTitle className={classes.dialogTitle}>
        <Subtitle hLevel={6}>Add New Card</Subtitle>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={2} justify='center'>
          <Grid item xs={12} sm={6}>
            <InputLabel htmlFor='full-name' shrink className={classes.label}>
              Full Name On Card
            </InputLabel>
            <Input
              id='full-name'
              type='text'
              classes={{ root: classes.inputRoot, input: classes.input, error: classes.error }}
              error={fullNameError}
              onChange={handleFullName}
              onBlur={handleBlurFullName}
              disableUnderline
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputLabel htmlFor='postal-code' shrink className={classes.label}>
              {isUSAUser ? 'Zip Code' : 'Postal Code'}
            </InputLabel>
            <Input
              id='postal-code'
              type='text'
              onChange={handlePostalCode}
              onBlur={handleBlurPostalCode}
              error={postalCodeError}
              classes={{ root: classes.inputRoot, input: classes.input, error: classes.error }}
              disableUnderline
            />
          </Grid>
        </Grid>

        <Grid container>
          <InputLabel htmlFor='cc-number' shrink className={classes.label}>
            Card Number
          </InputLabel>
          <Grid container className={classes.stripeInput}>
            <div ref={cardNumberRef} id='cc-number' className={classes.stripeRef} />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <InputLabel htmlFor='cc-expiry' shrink className={classes.label}>
              Expiration Date
            </InputLabel>
            <Grid container className={classes.stripeInput}>
              <div ref={cardExpiryRef} id='cc-expiry' className={classes.stripeRef} />
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <InputLabel htmlFor='cc-cvc' shrink className={classes.label}>
              CCV
            </InputLabel>
            <Grid container className={classes.stripeInput}>
              <div ref={cardCvcRef} id='cc-cvc' className={classes.stripeRef} />
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.dialogActionContianer}>
        <Button
          data-locator='primary-cta-button'
          color='primary'
          variant='contained'
          className={classes.dialogActionButton}
          disabled={addDisabled}
          onClick={handleAddCard}>
          Add
        </Button>
        <Button className={classes.dialogActionButton} onClick={handleCancel}>
          Cancel
        </Button>
      </DialogActions>
    </>
  );
}

export default CardDialog;
